import type { Durable, Option } from "@tento-lona/sheets";
import { nonnull } from "@tento-core/assert";
import { OrderedTree } from "@tento-core/ordered-tree";
import { Tracing } from "@tento-core/tracing";
import { TypedApi } from "@tento-core/typed-api";
import { Rational } from "@tento-core/rational";
import { planMoveAfter } from "./move-after.ts";
import { RowWriteError, SdkRequestError } from "../errors.ts";
import { AfterFailedWrite, afterFailedWrite } from "../client/after-failed-write.ts";
import { Network } from "../network.ts";
import {
  type AddRowSpec,
  Json,
  RowId,
  RowKey,
  RowLocalId,
  RowNetworkKey,
  RowPatch,
  type RowTypeName,
  type SurfaceKind,
  SheetId,
  SheetNetworkKey,
} from "@tento-lona/sheets";
import { type Sheet } from "@tento-lona/sheets/platform";
import { MoveWrites, RowWrites, sheetWiring, SurfaceAnchor } from "@tento-lona/sheets/internal-sdk";
import { SheetRow } from "./sheet-row.ts";
import { RowFieldWrites } from "./row-field-writes.ts";
import { Actor, type InvalidationSink, LayoutChanged, NewRowInserted, SheetStructureChanged } from "../invalidation.ts";
import type { SheetHandle } from "../client/sheet-handle.ts";
import type { SheetHost } from "../client/sheet-host.ts";

/** The wire type of a sheet's header / footer surface row (`ensureSurface`). */
const SURFACE_CONTAINER = "surface-container";

/** Where a row sits in the sheet tree: its parent (`null` at the top level) and position. */
type Placement = {
  readonly parent: Option<RowLocalId>;
  readonly position: Option<Rational>;
};

/** What an undo of a row delete restores (`RowAccessor.delete`): the held
 *  row (`null`: the list did not hold it), its wire row as confirmed
 *  (`null`: the registry did not hold it; a field a write in flight painted
 *  holds its confirmed value), and the top-level position its restore sends
 *  (confirmed: a move in flight is not part of it). */
interface Restorable {
  readonly row: Option<SheetRow.Handle>;
  readonly raw: Option<Network.SheetRowDb>;
  readonly position: Rational;
}

/**
 * The writes a landed delete abandoned of one row (`RowAccessor.#forget`):
 * `landed` resolves once each was answered, with what the server applied
 * after the delete (`Late`), and `answered` turns true then.
 */
type Abandoned = {
  answered: boolean;
  readonly landed: Promise<Late>;
};

/** What abandoned writes of a row landed after its delete: the newest move's placement (`null`: none), the row-field bodies in issue order. */
type Late = { readonly place: Option<Placement>; readonly bodies: readonly RowWrites.Fields[] };

/** Two placements are one: the same parent at the same position. */
function samePlacement(a: Placement, b: Placement): boolean {
  if (a.parent !== b.parent) return false;
  if (a.position == null || b.position == null) return a.position == null && b.position == null;
  return Rational.compare(a.position, b.position) === 0;
}

/**
 * How many more times a failed `move`'s recovery re-reads the sheet when a
 * structural write of the row (a newer move issued or accepted) reached it
 * while it read.
 */
const MOVE_REREADS = 2;

/**
 * How many more times an `add`'s, a `delete`'s or a `hydrateChildren`'s
 * recovery re-reads the sheet when a structural write reached a row it
 * decides while it read (`RowAccessor.#owe`).
 */
const STRUCTURAL_REREADS = 2;

/**
 * A failed structural write's recovery a newer structural write dropped
 * (`RowAccessor.#owe`): read again once no structural write of `rows` is in
 * flight.
 */
type OwedRecovery = {
  readonly verb: string;
  readonly rows: ReadonlySet<RowLocalId>;
  readonly run: () => Promise<void>;
};

/**
 * Fluent API for row operations on a sheet. Accessed via `sheet.rows`.
 */
/** Assigned from `RowAccessor`'s static block; see {@link rowAccessorLocal}. */
let localOf: (accessor: RowAccessor) => RowAccessor.Local;

/**
 * The local-only row-list seams of `accessor` (`RowAccessor.Local`). SDK-
 * private: `platformOf(handle).local` is the public door.
 *
 * @internal Not exported from `lib.ts`.
 */
export function rowAccessorLocal(accessor: RowAccessor): RowAccessor.Local {
  return localOf(accessor);
}

/** Assigned from `RowAccessor`'s static block; see {@link recoverCreatedRow}. */
let recoverCreatedOf: (accessor: RowAccessor, match: RowAccessor.CreatedMatch, verb: string, actor: Actor) => Promise<void>;

/**
 * After a create outside this accessor that may have stored (an
 * unanswered `SheetRow.Pending.promote` into its sheet), or a row linked
 * into its sheet (`client.rows.linkRow`): re-read the sheet and adopt each
 * top-level row matching `match` that the server lists and the accessor
 * does not — the recovery `add` runs (see `#recoverAdd`), its inserts
 * announced as `actor`. `verb` names the write in the trace a failed
 * re-read leaves.
 *
 * @internal SDK-private: not exported from `lib.ts`.
 */
export function recoverCreatedRow(accessor: RowAccessor, match: RowAccessor.CreatedMatch, verb: string, actor: Actor): Promise<void> {
  return recoverCreatedOf(accessor, match, verb, actor);
}

/**
 * A sheet handle's rows (`handle.rows`): the cache reads (`peek`, `list`) and
 * the persisting row verbs (`add`, `delete`, `move`).
 */
export class RowAccessor {
  static {
    localOf = (accessor) => ({
      adoptCreated: (created, actor) => accessor.#adoptCreated(created, actor),
      pushToList: (row) => accessor.#pushToList(row),
    });
    recoverCreatedOf = (accessor, match, verb, actor) => accessor.#recoverAdd(match, verb, STRUCTURAL_REREADS, actor);
  }

  /**
   * The `parent` of a `move` to the sheet's top level. `move` has no default
   * parent: a reorder within a parent names that parent, a move to the root
   * names this.
   */
  static readonly ROOT: unique symbol = Symbol("RowAccessor.ROOT");

  static readonly ACTOR = new Actor("rows-accessor");
  #entries: SheetRow.Handle[];
  readonly #sheetId: SheetNetworkKey;
  readonly #api: TypedApi<Network.Routes>;
  readonly #invalidation: InvalidationSink;
  readonly #handle: SheetHandle;
  /** The handle wiring beyond its public surface (see `RowAccessor.Wiring`). */
  readonly #wiring: RowAccessor.Wiring;
  /** The held row over a registered `SheetRow`: the sheet's interned facade of it (`Sheet.facadeFor`). */
  readonly #makeRow: (sheetRow: SheetRow) => SheetRow.Handle;
  /** The client registries a row added through `add` is interned + registered in. */
  readonly #registries: RowAccessor.Registries;
  readonly #host: SheetHost;
  /**
   * The moves `move` has in flight, per row (its stable identity, whatever
   * key a move addressed it by or a re-read answers it under): the one move
   * ledger (`MoveWrites`) — the tree shows the newest surviving move, else
   * the row's base (advanced by each move that landed, in issue order, and
   * replaced by a recanonicalized answer's snapshot); a refused move takes
   * back only its own move. A move's `seq` is its issue number among this
   * accessor's moves.
   */
  readonly #moves = new MoveWrites<RowLocalId, Placement>({
    name: "rows-accessor/moves",
    equals: samePlacement,
    describe: (id) => ({ sheetId: this.#sheetId, row: id.toString() }),
  });
  /**
   * A failed move's recovery read answered while the row's moves were
   * pending (`#rereadPlacement`): its answer could not be painted — the
   * newest move owns the paint — and the base may predate what the server
   * holds, so the sheet is read again once every move of the row settled,
   * with this many retries left.
   */
  readonly #owedRecovery = new Map<RowLocalId, number>();
  /** Per row: the issue number of its newest `move`. */
  readonly #lastMoveSeq = new Map<RowLocalId, number>();
  /** The move whose recanonicalized answer was installed last (`0`: none). */
  #installedSeq = 0;
  /** Rows this accessor deleted, by stable identity: no `move` answer or recovery may put them back. */
  readonly #deletedRows = new Set<RowLocalId>();
  /**
   * Per deleted row: the moves and row-field writes its landed delete
   * abandoned (`#forget`) — their late answers touch nothing; one that
   * lands after the restore's re-read opened owes the restored row a read.
   */
  readonly #abandoned = new Map<RowLocalId, Abandoned>();
  /**
   * The failed structural writes' re-reads in flight (`#rereadSheet`): each
   * collects the rows a structural write of this accessor touched — issued
   * or answered — while it was out (`#touch`), by their stable identity
   * (`RowLocalId`), so a write addressed by a key the row took while the
   * read was out (a re-key) still meets it. The re-read's answer may
   * predate such a write, so a recovery does not decide those rows from
   * it: it is owed again once that write settled (`#owe`). The one read
   * barrier of every recovery here: a move's (`#rereadPlacement`) as much
   * as an add's, a delete's or a hydrate's.
   */
  readonly #rereads = new Set<Set<RowLocalId>>();
  /**
   * Per row (its stable identity): the structural writes of this accessor
   * issued and not yet answered — a `move`, a `delete` (`#writing`). An
   * owed recovery waits for its rows to have none.
   */
  readonly #writesInFlight = new Map<RowLocalId, number>();
  /** The recoveries a newer structural write dropped, each owed once that write settled (`#owe`). */
  readonly #owed = new Set<OwedRecovery>();

  constructor(
    sheetId: SheetNetworkKey,
    list: SheetRow.Handle[],
    api: TypedApi<Network.Routes>,
    invalidation: InvalidationSink,
    handle: SheetHandle,
    wiring: RowAccessor.Wiring,
    makeRow: (sheetRow: SheetRow) => SheetRow.Handle,
    registries: RowAccessor.Registries,
    host: SheetHost,
  ) {
    this.#wiring = wiring;
    this.#sheetId = sheetId;
    this.#entries = list;
    this.#api = api;
    this.#invalidation = invalidation;
    this.#handle = handle;
    this.#makeRow = makeRow;
    this.#registries = registries;
    this.#host = host;
  }

  /**
   * The sheet's rows as the client holds them now, in list order. LOCAL
   * ONLY: no request. Iterate it, do not mutate it; read it again for the
   * rows added or deleted since (a handle's `rows` hands out its own views
   * of them, see `SheetHandle.rows`).
   */
  peekAll(): readonly SheetRow.Handle[] {
    return this.#entries;
  }

  /**
   * Register an externally-created row in the accessor's list — the list
   * only: the tree, the rows-side sheet and the invalidation are the caller's.
   * To adopt a row the server created, use `adoptCreated`. Reached through
   * `platformOf(handle).local` (`RowAccessor.Local`).
   */
  #pushToList(row: SheetRow.Handle): void {
    this.#entries.push(row);
  }

  /** A structural write reached row `rowKey`: no re-read out now may change it (`#rereads`). */
  #touch(rowKey: string): void {
    if (this.#rereads.size === 0) return;
    const id = this.#identityOf(rowKey);
    for (const touched of this.#rereads) touched.add(id);
  }

  /** The stable identity of the row `rowKey` names in the client's scope (every key it held resolves to it). */
  #identityOf(rowKey: string): RowLocalId {
    return this.#registries.ids.resolve(RowKey.of(rowKey));
  }

  /**
   * A structural write of row `rowKey` is issued: touched (`#touch`) and
   * counted in flight until the returned `settled` is called with its
   * answer — then every recovery it owed (`#owe`) whose rows have no write
   * in flight left starts.
   */
  #writing(rowKey: string): () => void {
    this.#touch(rowKey);
    const id = this.#identityOf(rowKey);
    this.#writesInFlight.set(id, (this.#writesInFlight.get(id) ?? 0) + 1);
    let settled = false;
    return () => {
      if (settled) return;
      settled = true;
      const left = (this.#writesInFlight.get(id) ?? 1) - 1;
      if (left > 0) {
        this.#writesInFlight.set(id, left);
        return;
      }
      this.#writesInFlight.delete(id);
      this.#runOwed();
    };
  }

  /**
   * A recovery's re-read answered while a structural write reached `rows`
   * (the `#rereads` barrier): its answer may predate that write, so it
   * decides nothing for them — and the write may yet be refused, leaving
   * the rows as the older answer could not show them. The recovery is owed
   * instead of dropped: `recover` runs again, with one re-read fewer left,
   * once no write of `rows` is in flight (now, when those writes settled
   * while the read was out), and only while the sheet is live. With no
   * re-read left the tree is left as is.
   */
  #owe(verb: string, rows: ReadonlySet<RowLocalId>, rereads: number, recover: (rereads: number) => Promise<void>): Promise<void> {
    if (rereads <= 0) return Promise.resolve();
    const run = (): Promise<void> => recover(rereads - 1);
    if (![...rows].some((id) => this.#writesInFlight.has(id))) return run();
    this.#owed.add({ verb, rows, run });
    return Promise.resolve();
  }

  /** Start every owed recovery (`#owe`) whose rows no structural write is in flight for — while the sheet is live. */
  #runOwed(): void {
    for (const owed of [...this.#owed]) {
      if ([...owed.rows].some((id) => this.#writesInFlight.has(id))) continue;
      this.#owed.delete(owed);
      if (!AfterFailedWrite.isLive(this.#wiring)) continue;
      void owed.run().catch((failure: unknown) =>
        Tracing.warn("net")(`${owed.verb}: the recovery owed after a newer write settled failed: ${String(failure)}`)
      );
    }
  }

  /**
   * Find a row by a tagged query — `{ id }`, `{ lookupKey }`, `{ type }` or
   * `{ label }`; `null` when no row matches.
   */
  peek(query: SheetHandle.RowQuery): Option<SheetRow.Handle> {
    if ("id" in query) {
      const key = query.id.toString();
      return this.#entries.find((r) => r.id.toString() === key) ?? null;
    }
    if ("lookupKey" in query) {
      return this.#entries.find((r) => r.lookupKey === query.lookupKey) ?? null;
    }
    if ("type" in query) {
      return this.#entries.find((r) => r.descriptor.id === query.type) ?? null;
    }
    return this.#entries.find((r) => r.label === query.label) ?? null;
  }

  /**
   * Add a new row to the sheet (`POST /api/sheets/{sheetKey}/row`) and wire
   * it into the local tree: a row of type `spec.type` whose own fields are
   * `spec.fields` (`RowPatch`: `label`, `icon`, `heightPx`, `formula`,
   * `timeScale`, `attributes` — a field it leaves out takes the type's
   * default; `Patch.clear` is the same as leaving it out), keyed by
   * `spec.lookupKey` (`null`: a row found by id only). The server places it
   * at the top level (the sheet's first position when it names none); move
   * it with `move`. Throws `RowWriteError` (nothing added locally) when the
   * server rejects the row or does not answer. Unanswered (or answered
   * unreadably: `SdkRequestError.mayHaveStored`), the row may have been
   * created: the sheet is re-read and a top-level row of the added `type`
   * (and `lookupKey`, when set) the server lists but the sheet does not is
   * adopted, announced as `RowAccessor.ACTOR` — the add still rejects.
   *
   * Once the server accepted the create, the row is listed whatever
   * follows: it is adopted as `adoptCreated` adopts one, and a local step
   * that fails after the acceptance (the rows-side mirror, an insert
   * observer that throws) is traced (`add-row/adopt-error`) instead of
   * turning an accepted add into a failure. It resolves once the create
   * landed (`RowAccessor.Added`): the created row, and `styled` — `null`
   * when the spec sets neither `icon` nor `heightPx`, which the create body
   * cannot carry; else those fields sent right after as the created row's
   * `updateDurable` (painted at once, taken back on a refusal), whose
   * `settled` rejects with `RowWriteError` (step `update`) when they were
   * refused or went unanswered — the row stays listed without them. A
   * rejected add never leaves a created row behind it: a caller that
   * retries after a rejection creates no second row it could not see.
   *
   * `actor` is the writing surface; the insert's `NewRowInserted` is
   * emitted by the sheet handle, scoped to this sheet, stamped with it.
   *
   * ```ts
   * const { row, styled } = await handle.rows.add(spec, actor);
   * styled?.settled.catch(report);
   * ```
   */
  async add(spec: RowAccessor.NewRow, actor: Actor): Promise<RowAccessor.Added> {
    this.#wiring.assertLive("rows.add");
    const { body, followUp } = RowAccessor.NewRow.encode(spec);
    const row = await this.#create(body, "rows.add", actor);
    const styled = followUp == null ? null : row.updateDurable(followUp, actor);
    return Object.freeze({ row, styled });
  }

  /**
   * Make sure the sheet's `kind` surface (its header or footer) is a
   * server-backed row, and answer that row: the surface row the sheet
   * already has — a server-backed `surface-container` row, or a surface
   * region an authored template declares — else a new `surface-container`
   * row created with the surface marker (`POST /api/sheets/{sheetKey}/row`,
   * `surface: kind`) and adopted as `add` adopts a row. Checked as `add` is:
   * a refused or unanswered create rejects with `RowWriteError` (step
   * `add`), and one that may have stored is recovered by the same re-read
   * (a top-level `surface-container` row the server lists but the sheet
   * does not is adopted). One create per `kind` is in flight: a call made
   * while one is out answers that one's promise. Parent rows under the
   * answered row with `move` (`{ parent: surface.id, position }`).
   *
   * `actor` is the writing surface; the created row's `NewRowInserted`
   * is stamped with it.
   */
  ensureSurface(kind: SurfaceKind, actor: Actor): Promise<SheetRow.Handle> {
    this.#wiring.assertLive("rows.ensureSurface");
    const inFlight = this.#surfaceCreates.get(kind);
    if (inFlight != null) return inFlight;
    const existing = this.#surfaceRow(kind);
    if (existing != null) return Promise.resolve(existing);
    const created = this.#createSurface(kind, actor).finally(() => {
      this.#surfaceCreates.delete(kind);
    });
    this.#surfaceCreates.set(kind, created);
    return created;
  }

  /** The surface creates `ensureSurface` has in flight, one per kind. */
  readonly #surfaceCreates = new Map<SurfaceKind, Promise<SheetRow.Handle>>();

  /**
   * The row the sheet's `kind` surface already is: a top-level row this
   * accessor lists (a row the server holds) whose rows-side row is a
   * server-backed `surface-container` or an authored surface region of
   * that kind — `null` when there is none yet. A synthetic placeholder the
   * rows-side sheet shows for the surface is not one: the server holds no
   * such row.
   */
  #surfaceRow(kind: SurfaceKind): Option<SheetRow.Handle> {
    const rowsSheet = this.#wiring.rowsSheet();
    for (const row of this.#entries) {
      const model = rowsSheet.peekRow(row.id);
      if (model == null || rowsSheet.parentOf(row.id) != null) continue;
      const anchor = SurfaceAnchor.of(model);
      if (anchor?.kind === kind && (anchor.source === "server" || anchor.source === "authored")) return row;
    }
    return null;
  }

  /** `ensureSurface`'s create: a `surface-container` row carrying the surface marker. */
  async #createSurface(kind: SurfaceKind, actor: Actor): Promise<SheetRow.Handle> {
    const body = Network.NewRowBody.make({
      type: SURFACE_CONTAINER,
      label: null,
      formula: null,
      timeScale: null,
      attributes: null,
      lookupKey: null,
      position: null,
      surface: kind,
      id: null,
    });
    return await this.#create(body, "rows.ensureSurface", actor);
  }

  /**
   * The checked create behind `add` and `ensureSurface`: promote the sheet
   * first (COW), post `body`, and adopt the created row — or, when the post
   * may have stored, re-read the sheet and adopt the row it created
   * (`#recoverAdd`) before rethrowing. `verb` names the write.
   */
  async #create(body: Network.NewRowBody, verb: string, actor: Actor): Promise<SheetRow.Handle> {
    // COW: an ephemeral sheet must persist before its first row lands, or the
    // row POSTs against an un-promoted sheet id and is orphaned. No-op once
    // synced. See `ephemeral-promotion` in the lona-sdk-2.0 project docs.
    await this.#handle.ensurePromoted();
    // Disposed while the promote was awaited: send nothing.
    this.#wiring.assertLive(verb);
    let result: Network.CreateRowResponse;
    try {
      result = await RowWriteError.check(
        "add",
        "POST",
        "/api/sheets/{sheetKey}/row",
        () =>
          this.#api.postResult("/api/sheets/{sheetKey}/row", {
            path: { sheetKey: SheetNetworkKey.fromWire(this.#sheetId) },
            body,
          }),
      );
    } catch (err) {
      // No verdict (or one the SDK could not read): the row may have been
      // created — re-read the sheet and adopt it.
      const lookupKey = body.lookupKey == null ? null : RowNetworkKey.parse(body.lookupKey);
      afterFailedWrite(err, this.#wiring, verb, () => this.#recoverAdd({ type: body.type, lookupKey }, verb, STRUCTURAL_REREADS, RowAccessor.ACTOR));
      throw err;
    }
    // Accepted: from here the create resolves with the row, whatever a
    // local step does.
    return this.#adoptCreated(result, actor);
  }

  /**
   * Adopt a row the server has already created — the add-row intent for a row
   * that did not come through `add` (a linked alias, a create another surface
   * made): register it with the client, list it here, mirror it into the
   * rows-side sheet, then insert it at the top level of the tree. The insert
   * runs LAST because it emits `NewRowInserted` (as `actor`), and that
   * dispatch synchronously rebinds the structure, which resolves every row's
   * rows-side `variant` — a row still missing there aborts the whole add.
   *
   * Idempotent per row: a row already listed, or already in the rows-side
   * sheet, is not added there twice. The server already holds the row, so
   * this never throws once the row is listed: a rows-side mirror that
   * throws (alias template expansion can) is logged
   * (`add-row/mirror-error`), and an insert whose announcement throws (an
   * observer of `NewRowInserted`) is logged (`add-row/adopt-error`); a
   * reload re-hydrates either. Returns the listed row.
   */
  #adoptCreated(
    created: Network.CreateRowResponse,
    actor: Actor,
  ): SheetRow.Handle {
    const rowKey = created.id.toString();
    this.#touch(rowKey);
    const localId = this.#registries.ids.resolve(RowKey.of(rowKey));
    this.#registries.rows.set(localId, created, null);
    // The registry holds what it was just given; should it not, the row is
    // still built from the server's answer rather than refused.
    const sheetRow = this.#registries.rows.get(localId) ?? SheetRow.from(localId, created, null);
    let row = this.peek({ id: localId });
    if (row == null) {
      row = this.#makeRow(sheetRow);
      this.#pushToList(row);
    }
    // The full row spec reaches the rows-side sheet before the insert, whose
    // own fallback mirror carries only `type` / `timeScale` — an alias row
    // without its template is refused there.
    this.#mirrorCreated(created);
    try {
      // `insertRoot` emits NewRowInserted internally, as `actor`.
      this.#wiring.insertRoot(
        localId,
        created.position ?? Rational.first(),
        actor,
      );
    } catch (e) {
      Tracing.log("add-row/adopt-error", {
        id: rowKey,
        type: created.type,
        error: e instanceof Error ? e.message : String(e),
      });
    }
    return row;
  }

  /**
   * The rows-side half of `add` and `adoptCreated`: the full row spec, once.
   * `peekRow` answers `null` for a row the rows-side sheet does not hold.
   */
  #mirrorCreated(created: Network.CreateRowResponse): void {
    const rowKey = created.id.toString();
    const rowsSheet = this.#wiring.rowsSheet();
    Tracing.log("add-row/mirror", {
      id: rowKey,
      type: created.type,
      lookupKey: created.lookupKey ?? null,
      template: Json.string(Json.object(created.attributes), "template"),
      hasRowsSheet: true,
    });
    if (rowsSheet.peekRow(rowsSheet.registry.resolveFromRawId(rowKey)) != null) return;
    // The wire's untyped JSON bags, kept only when they are objects.
    const attributes = Json.object(created.attributes);
    const meta = Json.object(created.metadata);
    const spec: AddRowSpec = {
      id: RowNetworkKey.fromWire(rowKey),
      type: created.type,
      ...(created.label != null ? { label: created.label } : {}),
      ...(created.formula != null ? { formula: created.formula } : {}),
      ...(created.lookupKey != null ? { lookupKey: created.lookupKey } : {}),
      ...(created.timeScale != null ? { timeScale: created.timeScale } : {}),
      ...(attributes != null ? { attributes } : {}),
      ...(meta != null ? { meta } : {}),
    };
    try {
      rowsSheet.addRow(spec);
    } catch (e) {
      Tracing.log("add-row/mirror-error", {
        id: rowKey,
        type: created.type,
        lookupKey: created.lookupKey ?? null,
        error: e instanceof Error ? e.message : String(e),
      });
    }
  }

  /**
   * Delete the held row from the sheet — a `SheetRow.Handle` or the
   * `SheetRow.Joined.Handle` a renderer holds; never re-resolved by id.
   *
   * Unconditional and silent: it asks no one and shows nothing. A caller
   * that confirms with its user does so before calling it, and the UI after
   * it (a "Row deleted" notice with its undo) is the caller's, built from
   * what this resolves (`RowAccessor.Deletion`):
   *
   * - `{ outcome: "deleted", undo }` — the server deleted the row and it is
   *   removed from the list, the tree and the cell cache. `undo(actor)`
   *   restores it on the server (`POST /api/rows/{rowKey}/restore`, linked
   *   back into the sheet at the top level, at the position the row — or,
   *   for a nested row, its top-level ancestor — held) and lists it again:
   *   the sheet is re-read and the row adopted where the server holds it
   *   (its insert announced as `actor`; when the re-read fails or does not
   *   list it, it is adopted from what the client held, at the position the
   *   restore sent), then the sheet's structure is announced as `actor`. It
   *   resolves with the listed row (`handle.rows.peek({ id: restored.id })`
   *   is it). It rejects with `RowWriteError` (step `restore`) when the
   *   server refuses or does not answer — unanswered
   *   (`SdkRequestError.mayHaveStored`), the sheet is re-read and a row the
   *   server lists again is adopted, announced as `RowAccessor.ACTOR` — and
   *   with `SheetHandle.DisposedError` once the sheet was disposed (nothing
   *   sent). A sheet disposed while the restore was out lists nothing: the
   *   undo resolves with the row it deleted.
   * - `{ outcome: "not-deletable", undo: null }` — the row cannot be deleted
   *   (`row.capabilities().canDelete` is false: a system row, a
   *   parent-stored virtual row); nothing is sent. Read `canDelete` first to
   *   offer the delete only where it can happen.
   *
   * A rejected or unanswered server delete throws `RowWriteError` and
   * removes nothing locally; unanswered (`SdkRequestError.mayHaveStored`),
   * the sheet is re-read, and a row the server no longer lists is removed
   * as a landed delete removes it, announced as `RowAccessor.ACTOR` (no
   * undo: the delete still rejects). A sheet disposed while the promote was pending
   * rejects with `SheetHandle.DisposedError`: nothing is sent or removed.
   */
  async delete(
    row: SheetRow.Handle | SheetRow.Joined.Handle,
    actor: Actor,
  ): Promise<RowAccessor.Deletion> {
    this.#wiring.assertLive("rows.delete");
    let rowId = row.id.toString();

    if (!row.capabilities().canDelete) return { outcome: "not-deletable", undo: null };
    // What an undo restores: the row as the client holds it, and the
    // top-level position its restore links it back at.
    let kept = this.#restorable(rowId);

    // COW: promote an ephemeral sheet before mutating it. No-op once synced.
    await this.#handle.ensurePromoted();
    this.#wiring.assertLive("rows.delete");
    // The promote re-keys the row: address it by the key it holds now.
    rowId = row.id.toString();
    const settled = this.#writing(rowId);
    // Settled whatever throws once the write is counted in flight — the
    // request or the local removal after it — so the recoveries it owes
    // (`#owe`) still run.
    try {
      // Delete on server — required: a failure leaves the row in place.
      try {
        await RowWriteError.check(
          "delete",
          "DELETE",
          "/api/sheets/{sheetKey}/row/{rowKey}",
          () =>
            this.#api.delResult("/api/sheets/{sheetKey}/row/{rowKey}", {
              path: { sheetKey: SheetNetworkKey.fromWire(this.#sheetId), rowKey: RowNetworkKey.fromWire(rowId) },
            }),
        );
      } catch (err) {
        settled();
        // No verdict (or one the SDK could not read): the delete may have
        // landed — re-read the sheet and drop the row when it is gone.
        afterFailedWrite(err, this.#wiring, "rows.delete", () => this.#recoverDelete(rowId, STRUCTURAL_REREADS));
        throw err;
      }
      const confirmed = this.#removeDeleted(rowId, actor);
      // The fields a write in flight painted go back to their confirmed
      // values in what an undo restores (the write was abandoned).
      if (kept.raw != null) kept = { ...kept, raw: { ...kept.raw, ...wireFields(confirmed) } };
    } finally {
      settled();
    }

    const undo = async (undoActor: Actor): Promise<SheetRow.Handle> => {
      this.#wiring.assertLive("rows.restore");
      try {
        await RowWriteError.check(
          "restore",
          "POST",
          "/api/rows/{rowKey}/restore",
          () =>
            this.#api.postResult("/api/rows/{rowKey}/restore", {
              path: { rowKey: RowNetworkKey.fromWire(rowId) },
              // Both named: the server links the row back into the sheet
              // (without them it only un-deletes it, in no sheet).
              body: Network.RestoreRequest.make({ sheetKey: SheetNetworkKey.fromWire(this.#sheetId), position: kept.position }),
            }),
        );
      } catch (err) {
        // No verdict (or one the SDK could not read): the restore may have
        // landed — re-read the sheet and list the row when it is back.
        afterFailedWrite(err, this.#wiring, "rows.restore", () => this.#recoverRestore(rowId));
        throw err;
      }
      // Restored: its moves apply again, and it is listed where the server holds it.
      this.#touch(rowId);
      const id = this.#identityOf(rowId);
      this.#deletedRows.delete(id);
      // A write the delete abandoned that lands from now on is not in the
      // re-read below: the restored row owes another read once it lands.
      const abandoned = this.#abandoned.get(id) ?? null;
      this.#abandoned.delete(id);
      const lateUnknown = abandoned != null && !abandoned.answered;
      const restored = await this.#listRestored(rowId, kept, undoActor);
      if (lateUnknown) {
        void abandoned.landed.then((late) => this.#installLate(id, late)).catch((failure: unknown) =>
          Tracing.warn("net")(`rows.restore: the re-read owed after an abandoned write landed failed: ${String(failure)}`)
        );
      }
      if (AfterFailedWrite.isLive(this.#wiring)) {
        this.#invalidation.emit(new SheetStructureChanged(undoActor, SheetId.parse(this.#sheetId)));
      }
      return restored;
    };
    return { outcome: "deleted", undo };
  }

  /**
   * What an undo of deleting `rowKey` restores (`delete`): the held row and
   * its wire row as the client holds them now, and the top-level position
   * the restore links it back at — its own, or, for a nested row, its
   * top-level ancestor's (`Rational.first()` when the tree places neither).
   */
  #restorable(rowKey: string): Restorable {
    const id = this.#identityOf(rowKey);
    const flat = this.#wiring.placement();
    // Confirmed placements: a move in flight (of the row or an ancestor) is
    // not where the server holds it, and the delete abandons it.
    const placed = (row: RowLocalId): Placement =>
      this.#moves.confirmed(row) ?? { parent: flat.parent.get(row) ?? null, position: flat.positions.get(row) ?? null };
    let top = id;
    for (let parent = placed(top).parent; parent != null; parent = placed(top).parent) top = parent;
    const raw = this.#registries.rows.get(id)?.raw ?? null;
    return {
      row: this.peek({ id }),
      // A copy: the registry's row is painted in place by later writes.
      raw: raw == null ? null : { ...raw },
      position: placed(top).position ?? Rational.first(),
    };
  }

  /**
   * List a row an accepted restore brought back (`delete`'s `undo`): re-read
   * the sheet (`#rereadSheet`) and adopt the row where the server holds it —
   * at the top level at its position, then under the row whose `children`
   * name it, if any — its insert announced as `actor`. When the re-read
   * fails or does not list it, it is adopted from what the client held
   * (`kept`) at the top level, at the position the restore sent. A sheet
   * disposed meanwhile lists nothing: the row `kept` held is answered.
   */
  async #listRestored(rowKey: string, kept: Restorable, actor: Actor): Promise<SheetRow.Handle> {
    const id = this.#identityOf(rowKey);
    // The restore is a lifecycle edge: the row's own fields are installed
    // from ONE answer — the re-read's, else what the client held confirmed —
    // into every display copy (the registry's row the interned facade
    // reads, the rows-side store), through the row-field ledger's read.
    const read = this.#rowWrites().beginRead(id);
    // Moves issued since the restore landed are newer than its answer.
    const seq = this.#moves.issued;
    let sheet: Option<Network.UserSheetWithRows> = null;
    try {
      sheet = (await this.#rereadSheet("rows.restore"))?.sheet ?? null;
    } catch (failure) {
      Tracing.warn("net")(`rows.restore: the re-read after a landed restore failed: ${String(failure)}`);
    }
    if (!AfterFailedWrite.isLive(this.#wiring)) {
      this.#rowWrites().endRead(read, null, actor);
      return kept.row ?? this.#makeRow(nonnull(this.#registries.rows.get(id), `rows.restore: row ${rowKey} is not registered`));
    }
    const held = sheet?.rows.find((row) => this.#names(row.id.toString(), id)) ?? null;
    const raw = held ?? (kept.raw == null ? null : { ...kept.raw, position: kept.position, children: null });
    this.#rowWrites().endRead(read, raw == null ? null : RowFieldWrites.fieldsOfWire(raw), actor);
    const listed = sheet == null ? null : this.#adoptListed(sheet, id, actor, seq);
    if (listed != null) return listed;
    if (raw != null) {
      const row = this.#adoptRestored({ ...raw, position: kept.position, children: null, shouldFetchData: null }, actor);
      this.#keepNewerMove(id, { parent: null, position: kept.position }, seq);
      return row;
    }
    // No wire row to adopt from: the held row is listed again as it was.
    const row = nonnull(kept.row, `rows.restore: row ${rowKey} was neither listed nor registered`);
    if (this.peek({ id }) == null) this.#pushToList(row);
    try {
      this.#wiring.insertRoot(id, kept.position, actor);
    } catch (e) {
      Tracing.log("add-row/adopt-error", { id: rowKey, type: null, error: e instanceof Error ? e.message : String(e) });
    }
    return row;
  }

  /**
   * Adopt the row `id` from the sheet the server answered (`#rereadSheet`):
   * at the top level at its position, then under the row whose `children`
   * name it (matched by identity, as `#rereadPlacement` matches), announced
   * as `actor` — or `null` when the answer does not list it.
   */
  #adoptListed(sheet: Network.UserSheetWithRows, id: RowLocalId, actor: Actor, seq: number): Option<SheetRow.Handle> {
    const held = sheet.rows.find((row) => this.#names(row.id.toString(), id)) ?? null;
    if (held == null) return null;
    const row = this.#adoptRestored({ ...held, shouldFetchData: null }, actor);
    const parent = sheet.rows.find((r) => r.children?.some((child) => this.#names(child.toString(), id)) ?? false) ?? null;
    const parentId = parent == null ? null : this.#registries.ids.resolve(RowKey.of(parent.id.toString()));
    if (parentId != null) this.#wiring.local().move(id, parentId, held.position, actor);
    this.#keepNewerMove(id, { parent: parentId, position: held.position }, seq);
    return row;
  }

  /**
   * The restored row's placement as the restore's answer holds it
   * (`restored`, as of move number `seq`) is its confirmed base: a move of
   * it issued after the restore landed and still in flight keeps the paint
   * (`MoveWrites.confirm`), so the row is put back where that move shows it.
   */
  #keepNewerMove(id: RowLocalId, restored: Placement, seq: number): void {
    if (!this.#moves.moving(id)) return;
    if (this.#moves.confirm(id, restored, seq) != null) return;
    const shown = this.#moves.shown(id);
    if (shown != null) this.#wiring.local().move(id, shown.parent, shown.position, RowAccessor.ACTOR);
  }

  /**
   * Adopt a row a restore brought back (`#listRestored`, `#recoverRestore`)
   * from `wire`: as `#adoptCreated` adopts one, except that a row the client
   * still has registered KEEPS its registered `SheetRow` — the sheet's
   * interned facade reads that object, and the restore's row-field read
   * already installed the answer's own fields into it. A row not registered
   * is adopted as created.
   */
  #adoptRestored(wire: Network.CreateRowResponse, actor: Actor): SheetRow.Handle {
    const rowKey = wire.id.toString();
    const localId = this.#registries.ids.resolve(RowKey.of(rowKey));
    const registered = this.#registries.rows.get(localId);
    if (registered == null) return this.#adoptCreated(wire, actor);
    this.#touch(rowKey);
    let row = this.peek({ id: localId });
    if (row == null) {
      row = this.#makeRow(registered);
      this.#pushToList(row);
    }
    this.#mirrorCreated(wire);
    try {
      this.#wiring.insertRoot(localId, wire.position ?? Rational.first(), actor);
    } catch (e) {
      Tracing.log("add-row/adopt-error", { id: rowKey, type: wire.type, error: e instanceof Error ? e.message : String(e) });
    }
    return row;
  }

  /** The client's row-field ledger (`RowWrites`): the one the rows-side sheet and every facade of its rows write through. */
  #rowWrites(): RowWrites {
    return sheetWiring(this.#wiring.rowsSheet()).rowWrites;
  }

  /**
   * What writes a landed delete abandoned landed after the restore's re-read
   * opened (`late`; nothing when none did): the server holds them, and that
   * answer may predate them. Each is installed as the server's answer about
   * its own fields — the row-field bodies through the row-field ledger's
   * read (laid over the base while newer writes are in flight), the move's
   * placement painted unless a newer move owns it — announced as the SDK
   * (`RowAccessor.ACTOR`); then the row is read again (`#rereadRestored`).
   * Nothing once the sheet was disposed or the row deleted again.
   */
  #installLate(id: RowLocalId, late: Late): Promise<void> {
    if (late.place == null && late.bodies.length === 0) return Promise.resolve();
    if (this.#deletedRows.has(id) || !AfterFailedWrite.isLive(this.#wiring)) return Promise.resolve();
    const ledger = this.#rowWrites();
    const registered = this.#registries.rows.get(id);
    for (const body of late.bodies) {
      if (ledger.project(id) != null) {
        ledger.confirm(id, body);
      } else if (registered != null) {
        const read = ledger.beginRead(id);
        ledger.endRead(read, { ...RowFieldWrites.fieldsOf(registered), ...body }, RowAccessor.ACTOR);
      }
    }
    const place = late.place;
    if (place != null && !this.#moves.moving(id) && !samePlacement(this.#placementOf(id), place)) {
      this.#wiring.local().move(id, place.parent, place.position, RowAccessor.ACTOR);
      this.#invalidation.emit(new LayoutChanged(RowAccessor.ACTOR));
    }
    return this.#rereadRestored(id);
  }

  /**
   * A write a landed delete abandoned was accepted after the restore's
   * re-read opened (`delete`'s `undo`): the server holds a body that answer
   * may predate. Read the sheet again — the row's own fields through the
   * row-field ledger's read, its placement as `#rereadPlacement` puts it —
   * announced as the SDK (`RowAccessor.ACTOR`). Nothing once the sheet was
   * disposed or the row deleted again.
   */
  async #rereadRestored(id: RowLocalId): Promise<void> {
    if (this.#deletedRows.has(id) || !AfterFailedWrite.isLive(this.#wiring)) return;
    const read = this.#rowWrites().beginRead(id);
    let reread: Option<{ readonly sheet: Network.UserSheetWithRows; readonly touched: ReadonlySet<RowLocalId> }> = null;
    try {
      reread = await this.#rereadSheet("rows.restore");
    } finally {
      const held = reread == null || this.#deletedRows.has(id)
        ? null
        : reread.sheet.rows.find((row) => this.#names(row.id.toString(), id)) ?? null;
      this.#rowWrites().endRead(read, held == null ? null : RowFieldWrites.fieldsOfWire(held), RowAccessor.ACTOR);
    }
    if (reread == null || this.#deletedRows.has(id) || this.#moves.moving(id) || reread.touched.has(id)) return;
    const held = reread.sheet.rows.find((row) => this.#names(row.id.toString(), id));
    if (held == null) return;
    const parent = reread.sheet.rows.find((row) => row.children?.some((child) => this.#names(child.toString(), id)) ?? false) ?? null;
    const parentId = parent == null ? null : this.#registries.ids.resolve(RowKey.of(parent.id.toString()));
    const shown = this.#placementOf(id);
    if (samePlacement(shown, { parent: parentId, position: held.position })) return;
    this.#wiring.local().move(id, parentId, held.position, RowAccessor.ACTOR);
    this.#invalidation.emit(new LayoutChanged(RowAccessor.ACTOR));
  }

  /**
   * A failed restore's recovery (`afterFailedWrite`): re-read the sheet;
   * when the server lists the row again the restore landed — it is no
   * longer deleted, it is adopted where the server holds it and the sheet's
   * structure announced, both as `RowAccessor.ACTOR`. Nothing when the
   * server does not list it, when the accessor lists it already, or once
   * the sheet was disposed.
   */
  async #recoverRestore(rowKey: string): Promise<void> {
    const id = this.#identityOf(rowKey);
    const read = this.#rowWrites().beginRead(id);
    const seq = this.#moves.issued;
    let reread: Option<{ readonly sheet: Network.UserSheetWithRows; readonly touched: ReadonlySet<RowLocalId> }> = null;
    let held: Option<Network.SheetRow> = null;
    try {
      reread = await this.#rereadSheet("rows.restore");
      held = reread == null || this.#lists(rowKey) ? null : reread.sheet.rows.find((row) => this.#names(row.id.toString(), id)) ?? null;
    } finally {
      this.#rowWrites().endRead(read, held == null ? null : RowFieldWrites.fieldsOfWire(held), RowAccessor.ACTOR);
    }
    if (reread == null || held == null) return;
    this.#deletedRows.delete(id);
    this.#abandoned.delete(id);
    this.#adoptListed(reread.sheet, id, RowAccessor.ACTOR, seq);
    this.#invalidation.emit(new SheetStructureChanged(RowAccessor.ACTOR, SheetId.parse(this.#sheetId)));
  }

  /**
   * Remove a row the server deleted from the list, the tree and the cell
   * cache, and announce the relayout as `actor`. The delete wins over any
   * move of the row still in flight: its answer no longer touches the tree.
   */
  #removeDeleted(rowId: string, actor: Actor): Option<RowWrites.Fields> {
    this.#touch(rowId);
    const localId = this.#identityOf(rowId);
    // No move answer paints the row from now on (`#settleMove`), nor may an
    // owed recovery put it back.
    this.#owedRecovery.delete(localId);
    this.#deletedRows.add(localId);
    const confirmed = this.#forget(localId);
    this.#entries = this.#entries.filter((r) => r.id !== localId && r.id.toString() !== rowId);
    // The tree's own relayout is the SDK's (never the deleter's echo).
    this.#wiring.local().delete(localId, RowAccessor.ACTOR);
    this.#wiring.removeCells(RowNetworkKey.fromWire(rowId));
    this.#invalidation.emit(new LayoutChanged(actor));
    return confirmed;
  }

  /**
   * A landed delete is the end of row `localId`'s life as the ledgers track
   * it: every move (`MoveWrites`) and row-field write (`RowWrites`) of it in
   * flight is abandoned (`forget`) — a late answer, accepted or refused,
   * paints nothing and sends no take-back. Kept until a restore (`#abandoned`):
   * one that lands after the restore's re-read opened owes the restored row
   * a read. Answers the row's confirmed fields (`null`: no row-field write
   * was in flight).
   */
  #forget(localId: RowLocalId): Option<RowWrites.Fields> {
    const moves = this.#moves.forget(localId);
    const fields = this.#rowWrites().forget(localId);
    if (moves.abandoned + fields.abandoned > 0) {
      const record: Abandoned = {
        answered: false,
        landed: Promise.all([moves.landed, fields.landed]).then(([place, bodies]) => {
          record.answered = true;
          return { place, bodies };
        }),
      };
      this.#abandoned.set(localId, record);
    }
    return fields.confirmed;
  }

  /**
   * Hydrate the virtual children of `row` (the held row, or its
   * `RowLocalId`) — materialize them as server-backed rows. Each row the
   * server created is registered with the client and listed here (`peek`,
   * `peekAll`), as `platformOf(handle).local.pushToList` lists one; the
   * tree's structure is the caller's. Resolves the created rows, each with
   * the `clientId` its request named. Throws `RowWriteError`
   * (`hydrate-children`) when the server refuses or does not answer —
   * nothing registered then; unanswered (`SdkRequestError.mayHaveStored`),
   * the sheet is re-read and the children of `row` it lists that this
   * accessor does not are registered and listed, announced as one
   * `SheetStructureChanged` (`RowAccessor.ACTOR`). `actor` stamps the
   * `SheetStructureChanged` a landed hydrate emits (the sheet's rows grew).
   */
  async hydrateChildren(
    row: SheetHandle.RowRef,
    children: Network.NewRowWithAttributes[],
    actor: Actor,
  ): Promise<RowAccessor.Hydrated[]> {
    this.#wiring.assertLive("rows.hydrateChildren");
    // COW: promote an ephemeral sheet before mutating it. No-op once synced.
    await this.#handle.ensurePromoted();
    this.#wiring.assertLive("rows.hydrateChildren");
    const parentKey = rowKeyOf(row);
    let answer: Network.HydrateChildrenResponse;
    try {
      answer = await RowWriteError.check(
        "hydrate-children",
        "POST",
        "/api/sheets/{sheetKey}/row/{rowKey}/hydrate-children",
        () =>
          this.#api.postResult(
            "/api/sheets/{sheetKey}/row/{rowKey}/hydrate-children",
            {
              path: { sheetKey: SheetNetworkKey.fromWire(this.#sheetId), rowKey: RowNetworkKey.fromWire(parentKey) },
              body: { children },
            },
          ),
      );
    } catch (err) {
      // No verdict (or one the SDK could not read): the children may have
      // been created — re-read the sheet and list them.
      afterFailedWrite(err, this.#wiring, "rows.hydrateChildren", () => this.#recoverHydrate(parentKey, STRUCTURAL_REREADS));
      throw err;
    }
    const hydrated = answer.hydratedRows.map((created) => ({
      clientId: created.clientId ?? null,
      row: this.#register(created.row),
    }));
    this.#invalidation.emit(new SheetStructureChanged(actor, SheetId.parse(this.#sheetId)));
    return hydrated;
  }

  /** Register a row the server created with the client and list it here
   *  (once); the listed row. */
  #register(created: Network.SheetRow): SheetRow.Handle {
    const rowKey = created.id.toString();
    this.#touch(rowKey);
    const localId = this.#registries.ids.resolve(RowKey.of(rowKey));
    this.#registries.rows.set(localId, created, null);
    const sheetRow = nonnull(
      this.#registries.rows.get(localId),
      `RowAccessor.hydrateChildren: row ${rowKey} did not register`,
    );
    const listed = this.peek({ id: localId });
    if (listed != null) return listed;
    const row = this.#makeRow(sheetRow);
    this.#pushToList(row);
    return row;
  }

  /**
   * Move `row` to just after the sibling `after` under `parent`
   * (`RowAccessor.ROOT`: the top level; `after` `null`: first), as shown
   * when the call is made. This is the drop a list makes: the caller names
   * a neighbour, not a position. One `move` when the neighbours'
   * positions leave room. When they tie (the server creates rows at one
   * default position, and the mediant of equal positions is equal), the
   * tied run around the drop is re-spaced instead: each of its rows, and
   * the moved row, is moved to a distinct position between the run's
   * untied neighbours, in the order shown with the drop applied
   * (`planMoveAfter`). The moves go out one after another. A refusal
   * rejects as `move` does, and the moves that landed before it stay.
   *
   * ```ts
   * await handle.rows.moveAfter(row, { parent: RowAccessor.ROOT, after: previousRow }, actor);
   * ```
   */
  async moveAfter(
    row: SheetHandle.RowRef,
    opts: { parent: SheetHandle.RowRef | typeof RowAccessor.ROOT; after: Option<SheetHandle.RowRef> },
    actor: Actor,
  ): Promise<void> {
    this.#wiring.assertLive("rows.moveAfter");
    const localId = this.#registries.ids.resolve(RowKey.of(rowKeyOf(row)));
    const parentLocalId = opts.parent === RowAccessor.ROOT
      ? null
      : this.#registries.ids.resolve(RowKey.of(rowKeyOf(opts.parent)));
    const afterLocalId = opts.after == null ? null : this.#registries.ids.resolve(RowKey.of(rowKeyOf(opts.after)));
    const flat = this.#wiring.placement();
    const shown = parentLocalId == null
      ? [...flat.positions.keys()].filter((id) => !flat.parent.has(id))
      : flat.children.get(parentLocalId) ?? [];
    const siblings = shown
      .filter((id) => id !== localId)
      .map((id) => ({ id, position: flat.positions.get(id) ?? null }));
    const plan = planMoveAfter(siblings, localId, afterLocalId);
    for (const step of plan) {
      await this.move(step.id, { parent: opts.parent, position: step.position }, actor);
    }
  }

  /**
   * Move/reparent a row.
   *
   * Orchestrates: optimistic local reparent → reorder on server → handle
   * recanonicalization → notify platform. A rejected or unanswered reorder
   * puts the row back under its previous parent at its previous position
   * and throws `RowWriteError`.
   *
   * Overlapping moves of one row: each move replaces the whole placement,
   * so the tree shows the newest move still standing. A failed move is
   * taken back only when it is the one shown — the row returns to the
   * newest surviving older move, else to where it was before the first
   * (the base, advanced by each move that landed, in issue order); a
   * failed move that a newer one already superseded changes nothing
   * locally. Once `delete` removed the row, a move's answer is ignored.
   *
   * `row` is the held row (or its `RowLocalId`, e.g. a row a fork just
   * re-keyed); `parent` is required — the new parent, or `RowAccessor.ROOT`
   * for the top level. A reorder within a parent names that parent:
   *
   * ```ts
   * await handle.rows.move(row, { parent: RowAccessor.ROOT, position }, actor);
   * ```
   */
  async move(
    row: SheetHandle.RowRef,
    opts: { parent: SheetHandle.RowRef | typeof RowAccessor.ROOT; position: Rational },
    actor: Actor,
  ): Promise<void> {
    this.#wiring.assertLive("rows.move");
    const a = actor;
    const localId = this.#registries.ids.resolve(RowKey.of(rowKeyOf(row)));
    const parentLocalId = opts.parent === RowAccessor.ROOT
      ? null
      : this.#registries.ids.resolve(RowKey.of(rowKeyOf(opts.parent)));

    // COW: promote an ephemeral sheet before reordering. No-op once synced.
    await this.#handle.ensurePromoted();
    this.#wiring.assertLive("rows.move");
    // The promote re-keys the rows: address the row and its parent by the
    // keys they hold now, never a key read before the await.
    const rowId: string = RowNetworkKey.of(localId);
    const parentRowKey: Option<string> = parentLocalId == null ? null : RowNetworkKey.of(parentLocalId);

    let move: Option<MoveWrites.Move<RowLocalId, Placement>> = null;
    if (!this.#deletedRows.has(localId)) {
      move = this.#moves.begin(localId, this.#placementOf(localId), { parent: parentLocalId, position: opts.position }).move;
      try {
        // `local.move` mirrors the structural change into the
        // canonical rows-side `Sheet` (see `mirrorReparentToRowsSheet`),
        // so `SheetRowTree`'s structure read reflects the new parent
        // without a full re-fetch.
        this.#wiring.local().move(localId, parentLocalId, opts.position, a);
      } catch (err) {
        // after-failed-write: nothing was sent — the local paint threw before the request, so the server stores nothing.
        // The move is taken back and was never counted in flight (`#writing`).
        this.#takeBackUnsent(localId, move, err);
        throw err;
      }
    }
    const seq = move?.seq ?? this.#moves.issued;
    this.#lastMoveSeq.set(localId, seq);

    // Counted in flight — and touched, the `#rereads` barrier — once
    // nothing local can refuse it (the request is issued next), and
    // settled whatever throws after, so the recoveries it owes (`#owe`)
    // still run.
    const settled = this.#writing(rowId);
    try {
      // Sync to server — required: a failure undoes the local reparent.
      let result: Network.RowReorderResponse;
      try {
        result = await RowWriteError.check(
          "reorder",
          "POST",
          "/api/sheets/{sheetKey}/row/{rowKey}/reorder",
          () =>
            this.#api.postResult("/api/sheets/{sheetKey}/row/{rowKey}/reorder", {
              path: { sheetKey: SheetNetworkKey.fromWire(this.#sheetId), rowKey: RowNetworkKey.fromWire(rowId) },
              body: Network.RowReorderBodySchema.make({
                position: opts.position,
                parentRowKey: parentRowKey == null ? null : RowNetworkKey.fromWire(parentRowKey),
                recanonicalizationThreshold: null,
              }),
            }),
        );
      } catch (err) {
        if (move != null) this.#settleMove(localId, move, false, err);
        settled();
        // No verdict (or one the SDK could not read): the move may have
        // landed — re-read where the server holds the row.
        afterFailedWrite(err, this.#wiring, "rows.move", () => this.#rereadPlacement(localId, MOVE_REREADS));
        throw err;
      }
      if (move != null) this.#settleMove(localId, move, true, null);
      this.#touch(rowId);
      settled();

      if (result.recanonicalizedSheet) {
        // The server renumbered the sheet's positions: every row takes the
        // answer's placement (`#installRecanonicalized`), so the next move
        // is computed from the server's numbering, not the stale one.
        if (!this.#deletedRows.has(localId)) this.#installRecanonicalized(result.recanonicalizedSheet, seq);
        this.#invalidation.emit(
          new SheetStructureChanged(a, SheetId.parse(this.#sheetId)),
        );
      } else {
        this.#invalidation.emit(new LayoutChanged(a));
      }
    } finally {
      settled();
    }
  }

  /**
   * Install the placements a move's recanonicalized answer carries (`sheet`,
   * the server's rows after it renumbered their positions; `seq`, that
   * move's issue number), announced as the SDK (`RowAccessor.ACTOR`). The
   * snapshot is the server's state once every move up to `seq` was
   * processed, so:
   * - an answer older than one already installed (a newer move's answer
   *   arrived first) is dropped — installing it would put back the numbering
   *   the newer one replaced;
   * - a row with moves in flight takes the snapshot as its `base`
   *   (`MoveWrites.confirm`), painted only when none of its moves is newer
   *   than `seq` — else the newest move keeps the paint;
   * - a row whose newest move (landed) is newer than `seq` keeps the
   *   placement that move gave it; a deleted row, a row the accessor does
   *   not know or under a parent it does not know is left alone.
   * Only rows whose parent or position differ are moved.
   */
  #installRecanonicalized(sheet: Network.UserSheetWithRows, seq: number): void {
    if (seq < this.#installedSeq) {
      Tracing.log("rows-accessor/recanonicalized-stale", { sheetId: this.#sheetId, seq, installed: this.#installedSeq });
      return;
    }
    this.#installedSeq = seq;
    const known = (key: string): Option<RowLocalId> => {
      const localId = this.#registries.ids.get(RowKey.of(key));
      return localId != null && this.peek({ id: localId }) != null ? localId : null;
    };
    const parentOf = new Map<string, string>();
    for (const row of sheet.rows) {
      for (const child of row.children ?? []) parentOf.set(child.toString(), row.id.toString());
    }
    const flat = this.#wiring.placement();
    for (const row of sheet.rows) {
      const localId = known(row.id.toString());
      if (localId == null || this.#deletedRows.has(localId)) continue;
      const parentKey = parentOf.get(row.id.toString()) ?? null;
      const parent = parentKey == null ? null : known(parentKey);
      if (parentKey != null && parent == null) continue;
      const placement: Placement = { parent, position: row.position };
      if (this.#moves.moving(localId)) {
        // The snapshot becomes the row's base; a move newer than it still
        // standing keeps the paint.
        if (this.#moves.confirm(localId, placement, seq) == null) continue;
      } else if ((this.#lastMoveSeq.get(localId) ?? 0) > seq) {
        continue;
      }
      const shownPosition = flat.positions.get(localId) ?? null;
      if (
        (flat.parent.get(localId) ?? null) === parent &&
        shownPosition != null && Rational.compare(shownPosition, row.position) === 0
      ) {
        continue;
      }
      this.#wiring.local().move(localId, parent, row.position, RowAccessor.ACTOR);
    }
  }

  /**
   * Take back a move whose local paint threw before its request was issued
   * (`move`): out of the row's queue, the row put where the newest
   * surviving move (else the base) has it. A take-back paint that throws
   * too is traced; the move's own failure is what `move` rejects with.
   */
  #takeBackUnsent(localId: RowLocalId, move: MoveWrites.Move<RowLocalId, Placement>, err: unknown): void {
    try {
      this.#settleMove(localId, move, false, err);
    } catch (failure) {
      Tracing.warn("net")(`rows.move: taking back a move whose local paint threw failed too: ${String(failure)}`);
    }
  }


  /**
   * A failed `move`'s recovery (`afterFailedWrite`): re-read the sheet
   * (`#rereadSheet`) and put the row where the server holds it — its
   * parent (the row whose `children` list it; the root when none does) and
   * position — announced as the SDK (`RowAccessor.ACTOR`). Nothing once
   * the sheet was disposed, once the row was deleted, nor for a row the
   * server does not list. A newer move of the row still in flight owns
   * its paint, and settling it may put back a base that predates what the
   * server holds: the recovery is kept, not dropped — the sheet is read
   * again once every move of the row settled (`#owedRecovery`). A
   * move of the row issued or accepted after the read began (the
   * `#rereads` barrier) may be missing from its snapshot: the snapshot is
   * never painted over it — the sheet is read again, at most `rereads`
   * more times, else the tree is left as is.
   */
  async #rereadPlacement(localId: RowLocalId, rereads: number): Promise<void> {
    const reread = await this.#rereadSheet("rows.move");
    if (reread == null || this.#deletedRows.has(localId)) return;
    if (this.#moves.moving(localId)) {
      this.#owedRecovery.set(localId, Math.max(this.#owedRecovery.get(localId) ?? 0, rereads));
      return;
    }
    if (reread.touched.has(localId)) {
      if (rereads > 0) await this.#rereadPlacement(localId, rereads - 1);
      return;
    }
    const { sheet } = reread;
    const held = sheet.rows.find((row) => this.#names(row.id.toString(), localId));
    if (held == null) return;
    // Its parent is the row whose `children` names it — matched by its
    // stable identity as `held` is (#names), so a child listed under a key
    // the row took while the read was out still finds its parent.
    const parent = sheet.rows.find((row) => row.children?.some((child) => this.#names(child.toString(), localId)) ?? false) ?? null;
    const parentLocalId = parent == null ? null : this.#registries.ids.resolve(RowKey.of(parent.id.toString()));
    this.#wiring.local().move(localId, parentLocalId, held.position, RowAccessor.ACTOR);
  }

  /**
   * A failed structural write's re-read (`GET /api/sheets/{sheetKey}`): the
   * sheet as the server holds it and the rows a structural write of this
   * accessor touched while the read was out (`touched`, by identity: the
   * answer may predate those writes, so a recovery owes them — `#owe`) — or
   * `null`: the server does not hold the sheet, or the sheet was disposed
   * while the read was out (its answer is dropped; `verb` names the write).
   */
  async #rereadSheet(verb: string): Promise<Option<{ readonly sheet: Network.UserSheetWithRows; readonly touched: ReadonlySet<RowLocalId> }>> {
    const touched = new Set<RowLocalId>();
    this.#rereads.add(touched);
    let sheet: Option<Network.UserSheetWithRows>;
    try {
      sheet = await SdkRequestError.checkRequest(
        "sheets",
        "get",
        "GET",
        "/api/sheets/{sheetKey}",
        () => this.#api.getResult("/api/sheets/{sheetKey}", { path: { sheetKey: SheetNetworkKey.fromWire(this.#sheetId) } }),
      ) ?? null;
    } finally {
      this.#rereads.delete(touched);
    }
    try {
      this.#wiring.assertLive(verb);
    } catch (_disposed) {
      return null;
    }
    return sheet == null ? null : { sheet, touched };
  }

  /**
   * Whether the accessor lists the row the answer's `rowKey` names — by
   * that key, or by the stable identity it resolves to (a key the row held
   * before a re-key).
   */
  #lists(rowKey: string): boolean {
    const id = this.#registries.ids.get(RowKey.of(rowKey));
    return this.#entries.some((r) => r.id.toString() === rowKey || (id != null && r.id === id));
  }

  /** Whether this accessor deleted the row the answer's `rowKey` names (its current key or one it held before a re-key). */
  #deletedKey(rowKey: string): boolean {
    const id = this.#registries.ids.get(RowKey.of(rowKey));
    return id != null && this.#deletedRows.has(id);
  }

  /** Whether the server's `rowKey` names the row `id` — its current key, or one it held before a re-key. */
  #names(rowKey: string, id: RowLocalId): boolean {
    return rowKey === id.toString() || this.#registries.ids.get(RowKey.of(rowKey)) === id;
  }

  /** Whether the answer's row `rowKey` is one a structural write reached while the read was out (`touched`, by identity). */
  #touchedIn(touched: ReadonlySet<RowLocalId>, rowKey: string): boolean {
    if (touched.size === 0) return false;
    const id = this.#registries.ids.get(RowKey.of(rowKey));
    return id != null && touched.has(id);
  }

  /**
   * A failed `add`'s (or `ensureSurface`'s, or an unanswered promote's:
   * `recoverCreatedRow`) recovery (`afterFailedWrite`):
   * re-read the sheet and adopt each top-level row of the added `type` (and
   * `lookupKey`, when the add named one) the server lists but this accessor
   * does not — the row the unanswered add created — as `adoptCreated`
   * adopts one (at the
   * position the server holds), announced as `actor` (`RowAccessor.ACTOR`
   * for a failed write's recovery). A row a
   * structural write touched while the read was out is not decided from
   * this answer (it may predate the write): the recovery is owed for it,
   * and runs again once that write settled (`#owe`, at most `rereads` more
   * times). Nothing once the sheet was disposed.
   */
  async #recoverAdd(attrs: RowAccessor.CreatedMatch, verb: string, rereads: number, actor: Actor): Promise<void> {
    const reread = await this.#rereadSheet(verb);
    if (reread == null) return;
    const { sheet, touched } = reread;
    const nested = new Set(sheet.rows.flatMap((row) => (row.children ?? []).map((child) => child.toString())));
    const owed = new Set<RowLocalId>();
    for (const row of sheet.rows) {
      const rowKey = row.id.toString();
      if (nested.has(rowKey) || this.#deletedKey(rowKey) || this.#lists(rowKey)) continue;
      if (attrs.type != null && row.type !== attrs.type) continue;
      if (attrs.lookupKey != null && row.lookupKey !== attrs.lookupKey) continue;
      if (this.#touchedIn(touched, rowKey)) {
        owed.add(this.#identityOf(rowKey));
        continue;
      }
      this.#adoptCreated({ ...row, shouldFetchData: null }, actor);
    }
    if (owed.size > 0) await this.#owe(verb, owed, rereads, (left) => this.#recoverAdd(attrs, verb, left, actor));
  }

  /**
   * A failed `delete`'s recovery (`afterFailedWrite`): re-read the sheet;
   * when the server no longer lists the row, the delete landed — remove it
   * as a landed delete does, announced as `RowAccessor.ACTOR`. Nothing when
   * the server still lists it, or once the sheet was disposed. When a
   * structural write touched the row while the read was out (a newer move
   * of it, a restore) the answer may predate it and decides nothing: the
   * recovery is owed, and runs again once that write settled (`#owe`, at
   * most `rereads` more times) — so a newer write the server refuses (the
   * row it addressed is gone) no longer leaves the deleted row listed. The
   * row is followed by its identity (`RowLocalId`), whatever key it took
   * while the read was out.
   */
  async #recoverDelete(rowId: string, rereads: number): Promise<void> {
    const id = this.#identityOf(rowId);
    const reread = await this.#rereadSheet("rows.delete");
    const rowKey = id.toString();
    if (reread == null || this.#deletedRows.has(id)) return;
    if (reread.touched.has(id)) {
      await this.#owe("rows.delete", new Set([id]), rereads, (left) => this.#recoverDelete(rowKey, left));
      return;
    }
    if (reread.sheet.rows.some((row) => this.#names(row.id.toString(), id))) return;
    if (!this.#entries.some((r) => r.id === id || r.id.toString() === rowKey)) return;
    this.#removeDeleted(rowKey, RowAccessor.ACTOR);
  }

  /**
   * A failed `hydrateChildren`'s recovery (`afterFailedWrite`): re-read the
   * sheet and register (and list) each child of the parent row the server
   * lists that this accessor does not, then announce one
   * `SheetStructureChanged` as `RowAccessor.ACTOR`. A child this accessor
   * deleted is left alone; nothing when there is none, or once the sheet
   * was disposed. A child a structural write touched while the read was
   * out is not decided from this answer (it may predate the write): the
   * recovery is owed for it, and runs again once that write settled
   * (`#owe`, at most `rereads` more times).
   */
  async #recoverHydrate(parentKey: string, rereads: number): Promise<void> {
    const parentId = this.#identityOf(parentKey);
    const reread = await this.#rereadSheet("rows.hydrateChildren");
    if (reread == null) return;
    const { sheet, touched } = reread;
    const parent = sheet.rows.find((row) => this.#names(row.id.toString(), parentId)) ?? null;
    const childKeys = new Set((parent?.children ?? []).map((child) => child.toString()));
    let grew = false;
    const owed = new Set<RowLocalId>();
    for (const row of sheet.rows) {
      const rowKey = row.id.toString();
      if (!childKeys.has(rowKey) || this.#deletedKey(rowKey) || this.#lists(rowKey)) continue;
      if (this.#touchedIn(touched, rowKey)) {
        owed.add(this.#identityOf(rowKey));
        continue;
      }
      this.#register(row);
      grew = true;
    }
    if (grew) {
      this.#invalidation.emit(new SheetStructureChanged(RowAccessor.ACTOR, SheetId.parse(this.#sheetId)));
    }
    if (owed.size > 0) {
      await this.#owe("rows.hydrateChildren", owed, rereads, (left) => this.#recoverHydrate(parentId.toString(), left));
    }
  }

  /** Where the tree shows row `localId` now: its parent and position. */
  #placementOf(localId: RowLocalId): Placement {
    const flat = this.#wiring.placement();
    return {
      parent: flat.parent.get(localId) ?? null,
      position: flat.positions.get(localId) ?? null,
    };
  }

  /**
   * Settle one move on its verdict (`MoveWrites`): a landed move folds into
   * the row's base once every older move settled; a failed one leaves the
   * ledger — and when it was the one shown, the row is put where the newest
   * surviving move (else the base) has it, announced as the SDK
   * (`RowAccessor.ACTOR`). Once no move of the row is left, a recovery read
   * owed while they were pending reads the sheet again
   * (`#rereadPlacement`; a failure is traced) — that bookkeeping runs even
   * when the take-back paint throws. A deleted row is never painted.
   */
  #settleMove(localId: RowLocalId, move: MoveWrites.Move<RowLocalId, Placement>, acked: boolean, err: unknown): void {
    if (acked) {
      this.#moves.accept(move);
      this.#afterMoves(localId);
      return;
    }
    const back = this.#moves.refuse(move, err);
    try {
      // Default actor: the surface that moved the row must repaint the undo.
      if (back != null && !this.#deletedRows.has(localId)) {
        this.#wiring.local().move(localId, back.to.parent, back.to.position, RowAccessor.ACTOR);
      }
    } finally {
      this.#afterMoves(localId);
    }
  }

  /** Once no move of row `localId` is in flight: the recovery owed while they were pending reads the sheet again. */
  #afterMoves(localId: RowLocalId): void {
    if (this.#moves.moving(localId)) return;
    const recovery = this.#owedRecovery.get(localId);
    if (recovery == null) return;
    this.#owedRecovery.delete(localId);
    if (this.#deletedRows.has(localId) || !AfterFailedWrite.isLive(this.#wiring)) return;
    void this.#rereadPlacement(localId, recovery).catch((failure: unknown) =>
      Tracing.warn("net")(`rows.move: the recovery owed after the row's moves settled failed: ${String(failure)}`)
    );
  }
}

/** A row's confirmed own fields (`RowWrites.Fields`) as wire row fields: those it holds (`null`: none). */
function wireFields(fields: Option<RowWrites.Fields>): Partial<Network.SheetRowDb> {
  if (fields == null) return {};
  const out: { -readonly [K in keyof Network.SheetRowDb]?: Network.SheetRowDb[K] } = {};
  if (fields.label !== undefined) out.label = fields.label;
  if (fields.icon !== undefined) out.icon = fields.icon;
  if (fields.heightPx !== undefined) out.heightPx = fields.heightPx;
  if (fields.formula !== undefined) out.formula = fields.formula;
  if (fields.timeScale != null) out.timeScale = fields.timeScale;
  if (fields.attributes !== undefined) out.attributes = fields.attributes;
  if (fields.lookupKey !== undefined) out.lookupKey = fields.lookupKey;
  return out;
}

/**
 * The row key a `SheetHandle.RowRef` is addressed by on the wire (as
 * `SheetHandle.RowRef.key` answers it; this module reads `client.ts` for
 * types only).
 */
function rowKeyOf(ref: SheetHandle.RowRef): string {
  return ref instanceof RowLocalId || ref instanceof RowId ? ref.toString() : ref.id.toString();
}

/**
 * Types that go with `RowAccessor`: `NewRow`, `CreatedMatch`, `Hydrated`,
 * `Wiring`, `Local`, `Registries`, and 1 more.
 */
export namespace RowAccessor {
  /**
   * The row `rows.add` creates: its wire type (`RowTypeName.of("number")`),
   * its own fields (`RowPatch` — the vocabulary `row.update` takes; a field
   * left out takes the type's default) and its lookup key
   * (`RowKey.keyed(":revenue")`; `null`: a row found by id only).
   *
   * ```ts
   * const row = await handle.rows.add(
   *   { type: RowTypeName.of("number"), fields: { label: "Weight", icon: "scale" }, lookupKey: null },
   *   actor,
   * );
   * ```
   */
  export type NewRow = {
    readonly type: RowTypeName;
    readonly fields: RowPatch;
    readonly lookupKey: Option<RowKey.Keyed>;
  };

  export namespace NewRow {
    /**
     * The wire form of `spec`: the create body (`POST
     * /api/sheets/{sheetKey}/row`) and the fields it cannot carry
     * (`icon`, `heightPx`), sent as the created row's `update` — `null`
     * when the spec sets neither. The fields are encoded once, by
     * `RowPatch.encode` (the row `update` verbs' encoder); a cleared field
     * (`Patch.clear`) is left out, as on a row that never had it.
     */
    export function encode(spec: NewRow): { readonly body: Network.NewRowBody; readonly followUp: Option<RowPatch> } {
      const wire = RowPatch.encode(spec.fields);
      const body = Network.NewRowBody.make({
        type: spec.type,
        label: wire.label ?? null,
        formula: wire.formula ?? null,
        timeScale: wire.timeScale ?? null,
        attributes: wire.attributes ?? null,
        lookupKey: spec.lookupKey == null ? null : spec.lookupKey.toString(),
        position: null,
        surface: null,
        id: null,
      });
      const followUp: RowPatch = {
        ...(wire.icon != null ? { icon: wire.icon } : {}),
        ...(wire.heightPx != null ? { heightPx: wire.heightPx } : {}),
      };
      return { body, followUp: Object.keys(followUp).length > 0 ? followUp : null };
    }
  }

  /**
   * What `rows.add` resolves once the create landed: the created row, and
   * `styled` — the created row's `updateDurable` of the fields the create
   * body cannot carry (`icon`, `heightPx`), `null` when the spec set
   * neither. Its `settled` rejects with `RowWriteError` (step `update`) when
   * they were refused; the row stays listed either way.
   */
  export type Added = {
    readonly row: SheetRow.Handle;
    readonly styled: Option<Durable<void>>;
  };

  /** The row a create that may have stored made (or a link listed), as its
   *  recovery finds it on the re-read sheet: a top-level row of `type`
   *  (`null`: of any type — a linked row, known by its lookup key), with
   *  `lookupKey` when the write named one. */
  export type CreatedMatch = { readonly type: Option<string>; readonly lookupKey: Option<RowNetworkKey> };

  /** A row `hydrateChildren` created: the `clientId` its request named
   *  (`null`: none) and the registered row. */
  export type Hydrated = { readonly clientId: Option<string>; readonly row: SheetRow.Handle };

  /**
   * What an accessor needs from its handle beyond `SheetHandle`'s public
   * surface. SDK-private: `SheetHandle.parse` supplies it; a test supplies a
   * fake.
   */
  export type Wiring = {
    /** Insert a registered row at the top level, announced as `actor`. */
    insertRoot(id: RowLocalId, position: Rational, actor: Actor): void;
    /** Evict a deleted row's cached cells. */
    removeCells(rowId: RowNetworkKey): void;
    /** Throw `SheetHandle.DisposedError` when the handle was disposed; `verb` names the attempt. */
    assertLive(verb: string): void;
    /** The handle's local-only tree mutations (`platformOf(handle).local`). */
    local(): SheetHandle.Local;
    /** Each row's parent and position in the handle's tree before resolution — the base a pending move starts from. */
    placement(): OrderedTree.Flattened<RowLocalId, Rational>;
    /** The full rows-side sheet: a created row's local mirror (`addRow`) is
     *  off the app face `handle.model` and the host's read face
     *  `platformOf(handle).sheet`. */
    rowsSheet(): Sheet;
  };

  /**
   * The local-only row-list seams, off the everyday `handle.rows` — reached
   * as `platformOf(handle).local` (`@tento/lona/host`).
   */
  export interface Local {
    /**
     * Adopt a row the server created elsewhere (a host's own POST): register
     * it with the client, list it, mirror it into the rows-side sheet and
     * insert it at the top level, announced as `actor`. Nothing is sent.
     */
    adoptCreated(created: Network.CreateRowResponse, actor: Actor): SheetRow.Handle;
    /** Register an externally created row in the list only (no tree, no announce). */
    pushToList(row: SheetRow.Handle): void;
  }

  /** The client-scoped registries a sheet's rows are interned and registered in. */
  export type Registries = {
    readonly ids: RowLocalId.Registry;
    readonly rows: SheetRow.Registry;
  };

  /**
   * What `delete` did with a held row: `"deleted"`, with the `undo` that
   * restores it; or `"not-deletable"` (nothing sent, no undo). See
   * `RowAccessor.delete`.
   */
  export type Deletion =
    | { readonly outcome: "deleted"; readonly undo: (actor: Actor) => Promise<SheetRow.Handle> }
    | { readonly outcome: "not-deletable"; readonly undo: null };

}
